/**
 * General AC power flow in the constraint-rule language — the step up from the
 * 2-bus toy in acModel.ts to a real network (PV buses, shunts, transformers).
 *
 * Every branch — line or transformer — is modelled by its 2x2 complex nodal
 * admittance (Yff, Yft, Ytf, Ytt), precomputed from the branch's series r/x, its
 * total line charging B, and its off-nominal tap ratio t:
 *
 *   Yff = (g + jb)/t² + j·B/2      Ytt = (g + jb) + j·B/2
 *   Yft = Ytf = −(g + jb)/t         (no phase shift)
 *
 * where g + jb = 1/(r + jx). A plain line is t = 1; a transformer has B = 0.
 * Splitting each Y into real/imag parts (Gff, Bff, Gft, Bft, Gtt, Btt — with
 * Gtf = Gft and Btf = Bft since there's no phase shift) gives the branch power
 * injections, with d = θ_from − θ_to:
 *
 *   P_from = Gff·Vf² + Vf·Vt·(Gft·cos d + Bft·sin d)
 *   Q_from = −Bff·Vf² + Vf·Vt·(Gft·sin d − Bft·cos d)
 *   P_to   = Gtt·Vt² + Vf·Vt·(Gft·cos d − Bft·sin d)
 *   Q_to   = −Btt·Vt² − Vf·Vt·(Gft·sin d + Bft·cos d)
 *
 * (The toy's g/b form is the special case Gff = Gtt = g, Bff = Btt = b,
 * Gft = Bft·−1… i.e. no charging and t = 1.)
 *
 * Bus roles follow standard power flow:
 *   - slack: V and θ fixed (θ = 0, V = setpoint);
 *   - PV (generator): V fixed at setpoint, real balance holds, Q is free (no
 *     reactive-balance equation — the generator supplies whatever Q it takes);
 *   - PQ (load): both real and reactive balance hold, V and θ free.
 *
 * As in the toy, the branch injections are auxiliary decision variables defined
 * by the four equalities above, and balance sums them. That keeps the system
 * square: for an N-bus, L-branch network, (2·slack + 2·PV + 2·PQ bus equations)
 * + 4·L branch definitions = the number of unknowns.
 */

import {
  Rule,
  RelationSchema,
  Catalog,
  Data,
  Expr,
  RuleSetSource,
  RuleProgram,
  bind,
  ref,
  attr,
  apply,
  call,
  num,
  str,
  add,
  sub,
  mul,
  div,
  pow,
  neg,
  cmp,
  eq,
  compare,
  range,
  define,
  sum,
  ruleToString,
  compileRuleSet,
  groundData,
} from '../language';
import { groundResiduals } from './residual';

// ---------------------------------------------------------------------------
// Network input
// ---------------------------------------------------------------------------

export type BusKind = 'slack' | 'pv' | 'pq';

export interface Bus {
  id: string;
  kind: BusKind;
  /** Voltage setpoint (slack and PV buses). */
  vSet?: number;
  /** Net real power injection, per unit: generation − load (non-slack buses). */
  pInj?: number;
  /** Net reactive power injection, per unit: −load (PQ buses; PV/slack are free). */
  qInj?: number;
  /** Fixed shunt susceptance at the bus, per unit (e.g. a capacitor bank). */
  shuntB?: number;
}

export interface Branch {
  id: string;
  from: string;
  to: string;
  /** Series resistance, per unit. */
  r: number;
  /** Series reactance, per unit. */
  x: number;
  /** Total line charging susceptance, per unit (split half to each end). */
  b?: number;
  /** Off-nominal tap ratio on the `from` side (1 for a plain line). */
  tap?: number;
}

// ---------------------------------------------------------------------------
// Derived admittances (definition rules)
// ---------------------------------------------------------------------------
// The six admittance terms are the real/imaginary parts of each branch's 2x2
// nodal admittance, computed from its series r/x, line charging b, and tap ratio
// — the same formulas that used to live in JS, now as editable definition rules
// over the branch's own columns. `groundData` constant-folds them at grounding.
//   y = g + jb = 1/(r + jx)  ⇒  g = r/(r²+x²),  b = −x/(r²+x²)
//   Yff = y/t² + jB/2,  Ytt = y + jB/2,  Yft = Ytf = −y/t

const brR = attr('l', 'r');
const brX = attr('l', 'x');
const brB = attr('l', 'b');
const brTap = attr('l', 'tap');
const denom = add(pow(brR, num(2)), pow(brX, num(2))); // r² + x²
const gSeries = div(brR, denom); // g = r/(r²+x²)
const bSeries = div(neg(brX), denom); // b = −x/(r²+x²)
const bHalf = div(brB, num(2)); // half the line charging
const tapSq = pow(brTap, num(2));

const branchRel = [bind('l', 'branch')];
const derived = (name: string, expr: Expr): Rule => ({ name, quantifiers: branchRel, body: define(expr) });

/** The admittance definition rules, computed from each branch's r/x/b/tap. */
export const AC_DERIVED_RULES: Rule[] = [
  derived('Gff', div(gSeries, tapSq)),
  derived('Bff', add(div(bSeries, tapSq), bHalf)),
  derived('Gtt', gSeries),
  derived('Btt', add(bSeries, bHalf)),
  derived('Gft', neg(div(gSeries, brTap))),
  derived('Bft', neg(div(bSeries, brTap))),
];

// ---------------------------------------------------------------------------
// Branch-injection expressions
// ---------------------------------------------------------------------------

const Vf = apply('V', attr('l', 'from'), ref('t'));
const Vt = apply('V', attr('l', 'to'), ref('t'));
const d = sub(apply('theta', attr('l', 'from'), ref('t')), apply('theta', attr('l', 'to'), ref('t')));
const cosd = call('cos', d);
const sind = call('sin', d);
const Gff = apply('Gff', ref('l'));
const Bff = apply('Bff', ref('l'));
const Gtt = apply('Gtt', ref('l'));
const Btt = apply('Btt', ref('l'));
const Gft = apply('Gft', ref('l'));
const Bft = apply('Bft', ref('l'));

/** Gff·Vf² + Vf·Vt·(Gft·cos d + Bft·sin d) */
const pFromExpr: Expr = add(
  mul(Gff, pow(Vf, num(2))),
  mul(mul(Vf, Vt), add(mul(Gft, cosd), mul(Bft, sind))),
);
/** −Bff·Vf² + Vf·Vt·(Gft·sin d − Bft·cos d) */
const qFromExpr: Expr = add(
  mul(neg(Bff), pow(Vf, num(2))),
  mul(mul(Vf, Vt), sub(mul(Gft, sind), mul(Bft, cosd))),
);
/** Gtt·Vt² + Vf·Vt·(Gft·cos d − Bft·sin d) */
const pToExpr: Expr = add(
  mul(Gtt, pow(Vt, num(2))),
  mul(mul(Vf, Vt), sub(mul(Gft, cosd), mul(Bft, sind))),
);
/** −Btt·Vt² − Vf·Vt·(Gft·sin d + Bft·cos d) */
const qToExpr: Expr = add(
  mul(neg(Btt), pow(Vt, num(2))),
  neg(mul(mul(Vf, Vt), add(mul(Gft, sind), mul(Bft, cosd)))),
);

// ---------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------

const branchTime = [bind('l', 'branch'), bind('t', 'timestep')];

const pFromRule: Rule = { name: 'p_from', quantifiers: branchTime, body: cmp('=', apply('P_from', ref('l'), ref('t')), pFromExpr) };
const qFromRule: Rule = { name: 'q_from', quantifiers: branchTime, body: cmp('=', apply('Q_from', ref('l'), ref('t')), qFromExpr) };
const pToRule: Rule = { name: 'p_to', quantifiers: branchTime, body: cmp('=', apply('P_to', ref('l'), ref('t')), pToExpr) };
const qToRule: Rule = { name: 'q_to', quantifiers: branchTime, body: cmp('=', apply('Q_to', ref('l'), ref('t')), qToExpr) };

/** Outflow of a branch quantity at bus n: from-end incidents plus to-end incidents. */
const outflow = (fromRel: string, toRel: string): Expr =>
  add(
    sum([bind('l', 'branch', eq(attr('l', 'from'), ref('n')))], apply(fromRel, ref('l'), ref('t'))),
    sum([bind('l', 'branch', eq(attr('l', 'to'), ref('n')))], apply(toRel, ref('l'), ref('t'))),
  );

/** Real balance at every non-slack bus: net injection == branch outflow. */
const pBalanceRule: Rule = {
  name: 'p_balance',
  quantifiers: [bind('n', 'bus', compare('!=', attr('n', 'kind'), str('slack'))), bind('t', 'timestep')],
  body: cmp('=', apply('p_inj', ref('n')), outflow('P_from', 'P_to')),
};

/**
 * Reactive balance at PQ buses only: net injection plus the shunt's B·V²
 * contribution == branch outflow. (PV and slack buses set Q free.)
 */
const qBalanceRule: Rule = {
  name: 'q_balance',
  quantifiers: [bind('n', 'bus', eq(attr('n', 'kind'), str('pq'))), bind('t', 'timestep')],
  body: cmp(
    '=',
    add(
      apply('q_inj', ref('n')),
      mul(apply('shunt_b', ref('n')), mul(apply('V', ref('n'), ref('t')), apply('V', ref('n'), ref('t')))),
    ),
    outflow('Q_from', 'Q_to'),
  ),
};

/** Slack bus: angle referenced to 0, magnitude held at setpoint. */
const slackAngleRule: Rule = {
  name: 'slack_angle',
  quantifiers: [bind('n', 'bus', eq(attr('n', 'kind'), str('slack'))), bind('t', 'timestep')],
  body: cmp('=', apply('theta', ref('n'), ref('t')), num(0)),
};
const slackVoltageRule: Rule = {
  name: 'slack_voltage',
  quantifiers: [bind('n', 'bus', eq(attr('n', 'kind'), str('slack'))), bind('t', 'timestep')],
  body: cmp('=', apply('V', ref('n'), ref('t')), attr('n', 'v_set')),
};
/** PV bus: magnitude held at setpoint (angle free, determined by real balance). */
const pvVoltageRule: Rule = {
  name: 'pv_voltage',
  quantifiers: [bind('n', 'bus', eq(attr('n', 'kind'), str('pv'))), bind('t', 'timestep')],
  body: cmp('=', apply('V', ref('n'), ref('t')), attr('n', 'v_set')),
};

// ---------------------------------------------------------------------------
// Variable declarations (ranged rules)
// ---------------------------------------------------------------------------
// These declare the decision relations (V, θ, and the branch flows) so the
// compiler knows them; the bounds themselves are nominal — Newton solves F(x)=0
// and doesn't enforce them (that's the ACOPF story). Kept wide so they never
// interfere.

const busTime = [bind('n', 'bus'), bind('t', 'timestep')];

const vBoundsRule: Rule = {
  name: 'v_bounds',
  quantifiers: busTime,
  body: range(num(0.5), apply('V', ref('n'), ref('t')), num(1.5)),
};
const thetaBoundsRule: Rule = {
  name: 'theta_bounds',
  quantifiers: busTime,
  body: range(neg(num(3.15)), apply('theta', ref('n'), ref('t')), num(3.15)),
};
const flowBoundsRule = (name: string, relation: string): Rule => ({
  name,
  quantifiers: branchTime,
  body: range(neg(num(100)), apply(relation, ref('l'), ref('t')), num(100)),
});
const pFromBoundsRule = flowBoundsRule('p_from_bounds', 'P_from');
const pToBoundsRule = flowBoundsRule('p_to_bounds', 'P_to');
const qFromBoundsRule = flowBoundsRule('q_from_bounds', 'Q_from');
const qToBoundsRule = flowBoundsRule('q_to_bounds', 'Q_to');

/** The variable-declaring rules, in tree order. */
export const AC_VARIABLE_RULES: Rule[] = [
  vBoundsRule,
  thetaBoundsRule,
  pFromBoundsRule,
  pToBoundsRule,
  qFromBoundsRule,
  qToBoundsRule,
];

/** The equality (residual) rules — the physics. */
export const AC_CONSTRAINT_RULES: Rule[] = [
  slackAngleRule,
  slackVoltageRule,
  pvVoltageRule,
  pFromRule,
  qFromRule,
  pToRule,
  qToRule,
  pBalanceRule,
  qBalanceRule,
];

/**
 * The whole AC power-flow rule set, in grounding order: the admittance
 * definitions, the variable declarations, then the equalities.
 */
export const AC_PF_RULES: Rule[] = [
  ...AC_DERIVED_RULES,
  ...AC_VARIABLE_RULES,
  ...AC_CONSTRAINT_RULES,
];

// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------

const dataRel = (
  name: string,
  keys: string[],
  values: string[],
  refs?: Record<string, string>,
): RelationSchema => ({ name, keys, values, kind: 'data', refs });

/**
 * The base data relations the AC rules are compiled against — the input tables,
 * kept deliberately small. The decision relations (V, θ, branch flows) are
 * derived from the variable rules by `compileRuleSet`, and the admittances
 * (Gff…Bft) from the definition rules — neither is a base input. Each branch
 * carries its own series r/x, line charging b, and tap ratio; the admittances
 * are computed from those in the language, not in JS.
 */
export const AC_BASE_CATALOG: Catalog = Object.fromEntries(
  [
    dataRel('bus', ['id'], ['kind', 'v_set']),
    dataRel('timestep', ['t'], []),
    dataRel('branch', ['id'], ['from', 'to', 'r', 'x', 'b', 'tap'], { from: 'bus', to: 'bus' }),
    dataRel('p_inj', ['bus'], ['value']),
    dataRel('q_inj', ['bus'], ['value']),
    dataRel('shunt_b', ['bus'], ['value']),
  ].map((r) => [r.name, r]),
);

/**
 * Plain-language descriptions of the AC relations, shown above their DATA tables.
 * Kept as an overlay (not on the schemas) so it covers the derived admittances
 * too — their schemas are synthesized by the compiler from the definition rules.
 */
export const AC_DESCRIPTIONS: Record<string, string> = {
  bus: 'A bus (node): its role — slack, PV (a generator holding a voltage), or PQ (a load) — and voltage setpoint.',
  branch:
    'A branch — a transmission line or transformer — joining two buses, with its series resistance/reactance (r, x), line charging (b), and tap ratio.',
  Gff: 'From-end self-conductance — Re(Yff), the branch admittance seen at the from bus. Derived from r, x, charging and tap.',
  Bff: 'From-end self-susceptance — Im(Yff); includes half the line charging.',
  Gtt: 'To-end self-conductance — Re(Ytt), the admittance seen at the to bus.',
  Btt: 'To-end self-susceptance — Im(Ytt); includes half the line charging.',
  Gft: 'Mutual conductance — Re(Yft), coupling the from and to buses.',
  Bft: 'Mutual susceptance — Im(Yft), coupling the from and to buses.',
  p_inj: 'Net real power injected at the bus (per unit): generation minus load.',
  q_inj:
    'Net reactive power injected at the bus (per unit): −load. PV and slack buses set their own reactive power.',
  shunt_b:
    'Fixed shunt susceptance at the bus (per unit), e.g. a capacitor bank; adds b·V² to the reactive balance.',
};

/** A copy of `catalog` with the AC descriptions overlaid onto matching relations. */
export function describeAcCatalog(catalog: Catalog): Catalog {
  const out: Catalog = {};
  for (const [name, schema] of Object.entries(catalog)) {
    const description = AC_DESCRIPTIONS[name];
    out[name] = description ? { ...schema, description } : schema;
  }
  return out;
}

/**
 * Ground a network into its residual system through the full pipeline — compile
 * the default rules, derive the admittances, then ground the equalities into
 * residuals. A convenience for callers (and tests) that don't hold a program.
 */
export function groundAcNetwork(buses: Bus[], branches: Branch[]) {
  const program: RuleProgram = compileRuleSet(defaultAcRuleSetSource(), AC_BASE_CATALOG).program;
  const data = groundData(program, buildAcPfData(buses, branches));
  return groundResiduals(program.constraintRules, program.catalog, data);
}

/**
 * The starting ACPF rule set as editable text — the authored rules rendered to
 * source, the same way DCOPF seeds its editable rules. From here the user edits
 * the text; the ASTs above stay the definition of the *default*.
 */
export function defaultAcRuleSetSource(): RuleSetSource {
  return {
    rules: AC_PF_RULES.map((rule) => ({
      id: rule.name,
      name: rule.name,
      source: ruleToString(rule),
      enabled: true,
    })),
    // AC power flow is feasibility — no objective, so no cost categories.
    costs: [],
  };
}

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

/**
 * Build the base {@link Data} for an AC network at a single timestep — the small
 * input set: bus roles/setpoints, branch topology *and its r/x/b/tap*, and the
 * per-bus injections. The admittances are computed from the branch columns by
 * the definition rules ({@link AC_DERIVED_RULES}) during grounding, not here.
 */
export function buildAcPfData(buses: Bus[], branches: Branch[]): Data {
  return {
    timestep: [{ t: 0 }],
    bus: buses.map((b) => ({ id: b.id, kind: b.kind, v_set: b.vSet ?? 1.0 })),
    branch: branches.map((br) => ({
      id: br.id,
      from: br.from,
      to: br.to,
      r: br.r,
      x: br.x,
      b: br.b ?? 0,
      tap: br.tap ?? 1,
    })),
    p_inj: buses.map((b) => ({ bus: b.id, value: b.pInj ?? 0 })),
    q_inj: buses.map((b) => ({ bus: b.id, value: b.qInj ?? 0 })),
    shunt_b: buses.map((b) => ({ bus: b.id, value: b.shuntB ?? 0 })),
  };
}

/**
 * A flat start seeded with the known voltage setpoints: slack/PV buses at their
 * setpoint, everything else at V = 1.0, all angles and branch flows at 0.
 */
export function flatStartFor(buses: Bus[], variables: string[]): Map<string, number> {
  const vSetById = new Map(buses.map((b) => [b.id, b.vSet]));
  const seed = new Map<string, number>();
  for (const v of variables) {
    // Variable names are like `V_bus1_t0`; recover the bus id between prefix and _t.
    const m = /^V_(.+)_t\d+$/.exec(v);
    const setpoint = m ? vSetById.get(m[1]) : undefined;
    seed.set(v, v.startsWith('V_') ? setpoint ?? 1.0 : 0.0);
  }
  return seed;
}
